<template>
  <v-card color="#121212" class="rounded-xl border-glow" flat>
    <div class="pa-5">
      <div class="d-flex align-center mb-4">
        <v-avatar color="red darken-3" size="40" class="mr-3 elevation-4">
          <v-icon small color="white">mdi-file-cancel</v-icon>
        </v-avatar>
        <div>
          <h3 class="text-h6 font-weight-bold white--text">Kündigungs-Assistent</h3>
          <div class="caption grey--text">AI hat "Kündigung" erkannt</div>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon small color="grey" @click="resetWizard" v-if="step > 1">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>

      <v-divider class="grey darken-3 mb-4"></v-divider>

      <div v-if="step === 0">
        <p class="body-2 grey--text text--lighten-1 mb-3">Welchen Vertrag möchte {{ user.fullName }} kündigen?</p>

        <div v-if="loadingContracts" class="text-center py-4">
          <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
        </div>

        <div v-else-if="rawContracts.length === 0" class="text-center py-4 error--text">Keine aktiven Verträge im TAC gefunden.</div>

        <v-list v-else color="transparent" dark dense two-line>
          <v-list-item-group v-model="selectedContractIndex" color="primary">
            <v-list-item
              v-for="(contract, i) in activeRawContracts"
              :key="i"
              class="mb-2 rounded-lg"
              style="background: rgba(255, 255, 255, 0.05)"
              @click="selectContract(contract)">
              <v-list-item-avatar>
                <v-icon :color="getContractColor(contract)">
                  {{ contract.CONTRACTTYP === "Hauptvertrag" ? "mdi-card-account-details-star" : "mdi-puzzle" }}
                </v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ contract.NAME }}</v-list-item-title>
                <v-list-item-subtitle class="caption">
                  <span :class="contract.CONTRACTTYP === 'Hauptvertrag' ? 'primary--text' : 'grey--text'">{{ contract.CONTRACTTYP }}</span>
                  | Bis: {{ formatDate(contract.TERM_ENDDATE) }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon small color="grey">mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </div>

      <div v-else-if="step === 1">
        <div class="d-flex align-center mb-3">
          <v-btn color="white" icon small @click="step = 0" class="mr-2"><v-icon>mdi-arrow-left</v-icon></v-btn>
          <span class="font-weight-bold white--text text-truncate">{{ activeContract.NAME }}</span>
        </div>

        <v-alert :color="isLate ? 'orange darken-4' : 'green darken-3'" dense text class="mb-4 rounded-lg">
          <div class="d-flex align-start justify-space-between" style="width: 100%">
            <div class="d-flex align-start">
              <v-icon :color="isLate ? 'orange' : 'green'" left>
                {{ isLate ? "mdi-clock-alert" : "mdi-check-circle" }}
              </v-icon>
              <div>
                <div class="font-weight-bold text-caption">{{ isLate ? "Verspätet / Automatische Verlängerung" : "Fristgerecht" }}</div>
                <div class="caption">
                  Kündigung per: <strong>{{ formattedTerminationDate }}</strong>
                </div>
              </div>
            </div>
            <v-btn v-if="isLate" x-small text color="white" class="ml-2" style="opacity: 0.8" @click="forceOnTime">
              <v-icon left small>mdi-gavel</v-icon> Trotzdem OK
            </v-btn>
          </div>
        </v-alert>

        <div v-if="isMainContract && allAddons.length > 0" class="mb-4 pa-3 rounded-lg border-subtle" style="background: rgba(255, 100, 0, 0.05)">
          <div class="caption orange--text text--lighten-1 mb-1 font-weight-bold">
            <v-icon x-small color="orange lighten-1" class="mr-1">mdi-alert-circle-outline</v-icon>
            Achtung: Komplettes Abo
          </div>
          <div class="caption grey--text mb-2">Mit diesem Hauptvertrag werden <strong>automatisch</strong> folgende Zusatzpakete gekündigt:</div>
          <div v-for="addon in allAddons" :key="addon.CONTRACTID" class="d-flex align-center py-0 pl-2">
            <v-icon x-small color="grey" class="mr-2">mdi-link</v-icon>
            <span class="caption white--text">{{ addon.NAME }}</span>
          </div>
        </div>

        <div v-else-if="!isMainContract && otherAddons.length > 0" class="mb-4 pa-3 rounded-lg" style="background: rgba(255, 255, 255, 0.03)">
          <div class="caption grey--text mb-2 font-weight-bold">Weitere Zusatzpakete ebenfalls kündigen?</div>
          <div v-for="addon in otherAddons" :key="addon.CONTRACTID" class="d-flex align-center py-1">
            <v-checkbox v-model="selectedAddonIds" :value="addon.CONTRACTID" hide-details dense class="ma-0 pa-0" color="red lighten-1"></v-checkbox>
            <div class="ml-2 overflow-hidden">
              <div class="body-2 white--text text-truncate">{{ addon.NAME }}</div>
              <div class="caption grey--text" style="font-size: 10px">{{ formatDate(addon.TERM_ENDDATE) }}</div>
            </div>
          </div>
        </div>

        <p class="caption grey--text mb-2">Kündigungsgrund wählen:</p>
        <v-select
          v-model="selectedReasonId"
          :items="reasons"
          item-text="Name"
          item-value="ReasonID"
          outlined
          dark
          dense
          rounded
          background-color="#1A1A1A"
          placeholder="Grund wählen..."
          :loading="loadingDetails"></v-select>

        <div v-if="isLate && !contractToTerminate" class="caption red--text mb-3">
          Fehler: Kein Folgevertrag für die verspätete Kündigung gefunden.
        </div>

        <v-btn block depressed color="primary" class="rounded-lg" :disabled="!selectedReasonId || !contractToTerminate" @click="step = 2">
          Weiter zur Prüfung
        </v-btn>
      </div>

      <div v-else-if="step === 2">
        <div class="text-center py-2">
          <p class="body-2 white--text mb-4">
            Möchtest du <strong>{{ contractsToProcessPreview.length }} Vertrag/Verträge</strong> im TAC System jetzt verbindlich kündigen?
          </p>

          <div class="mb-4 text-left px-4 py-2 rounded-lg" style="background: rgba(255, 255, 255, 0.02)">
            <div class="caption grey--text mb-1">Wird gekündigt:</div>
            <div v-for="(c, idx) in contractsToProcessPreview" :key="idx" class="caption white--text d-flex">
              <span class="mr-2">•</span>
              <span>{{ c.NAME }}</span>
              <span v-if="c.CONTRACTTYP !== 'Hauptvertrag' && isMainContract" class="grey--text ml-2">(Auto)</span>
            </div>
          </div>

          <div class="d-flex justify-space-between px-4">
            <v-btn text small color="grey" @click="step = 1">Zurück</v-btn>
            <v-btn color="red darken-1" depressed class="rounded-lg white--text" :loading="processing" @click="executeTermination">
              <v-icon left small>mdi-delete-forever</v-icon> Verbindlich Kündigen
            </v-btn>
          </div>
        </div>
      </div>

      <div v-else-if="step === 3">
        <v-alert type="success" dense text icon="mdi-check-circle" class="mb-4 rounded-lg"> Kündigung erfolgreich eingetragen! </v-alert>

        <p class="caption grey--text mb-1">Antwort an Kunden:</p>
        <v-card dark color="#000" class="pa-3 mb-3 rounded-lg border-subtle">
          <v-textarea
            v-model="mail.text"
            hide-details
            auto-grow
            rows="6"
            background-color="transparent"
            flat
            solo
            class="body-2 white--text"
            style="font-family: 'JetBrains Mono', monospace; line-height: 1.5"></v-textarea>
        </v-card>

        <div class="d-flex flex-wrap gap-2 mb-3">
          <v-chip dark color="grey darken-3" small label @click="previewPdf">
            <v-icon left x-small color="red">mdi-file-pdf-box</v-icon> PDF Vorschau
            <v-progress-circular v-if="pdfLoading" indeterminate size="12" width="2" class="ml-2"></v-progress-circular>
          </v-chip>
        </div>

        <div class="d-flex justify-end">
          <v-btn text small color="grey" class="mr-2" @click="$emit('cancel')">Abbrechen</v-btn>
          <v-btn color="success" depressed class="rounded-lg gradient-btn" :loading="sendingMail" @click="sendTerminationEmail">
            <v-icon left small>mdi-send</v-icon> Bestätigung Senden
          </v-btn>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import tacApi from "@/api/tacApi"
import api from "@/api"
import { format, addDays, addYears, isAfter } from "date-fns"
import { mapState } from "vuex"

export default {
  name: "MailTerminationWizard",
  props: {
    user: Object,
  },
  data: () => ({
    roodyUser: null,

    step: 0,
    loadingContracts: false,
    loadingDetails: false,
    processing: false,
    pdfLoading: false,
    sendingMail: false,

    rawContracts: [],
    selectedContractIndex: null,
    activeContract: null,
    contractToTerminate: null, // Der Vertrag der technisch gekündigt wird (bei Late = Folgevertrag)

    reasons: [],
    selectedReasonId: null,
    isLate: false,
    terminationDate: null,

    selectedAddonIds: [], // Für manuelle Auswahl bei Zusatzverträgen
    terminatedContractsResult: [], // Ergebnisliste für PDF

    mail: {
      to: "",
      subject: "Kündigungsbestätigung well come FIT",
      text: "",
    },
  }),

  computed: {
    ...mapState("studioStore", ["storeStudios"]),

    // Alle aktiven Verträge (Haupt UND Zusatz)
    activeRawContracts() {
      if (!this.rawContracts) return []
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      // Filtern: Nur was noch läuft oder in Zukunft läuft
      return (
        this.rawContracts
          .filter((c) => {
            const end = new Date(c.TERM_ENDDATE)
            return end >= today
          })
          // Sortierung: Hauptverträge zuerst, dann nach Enddatum
          .sort((a, b) => {
            if (a.CONTRACTTYP === "Hauptvertrag" && b.CONTRACTTYP !== "Hauptvertrag") return -1
            if (a.CONTRACTTYP !== "Hauptvertrag" && b.CONTRACTTYP === "Hauptvertrag") return 1
            return new Date(a.TERM_ENDDATE) - new Date(b.TERM_ENDDATE)
          })
      )
    },

    isMainContract() {
      return this.activeContract?.CONTRACTTYP === "Hauptvertrag"
    },

    // Liste aller Zusatzpakete (für Anzeige bei Hauptvertrag-Kündigung)
    allAddons() {
      if (!this.activeRawContracts) return []
      return this.activeRawContracts.filter((c) => c.CONTRACTTYP === "Zusatzvertrag")
    },

    // Liste anderer Zusatzpakete (für Auswahl bei Zusatzvertrag-Kündigung)
    otherAddons() {
      if (!this.activeRawContracts || !this.activeContract) return []
      return this.activeRawContracts.filter((c) => c.CONTRACTTYP === "Zusatzvertrag" && c.CONTRACTID !== this.activeContract.CONTRACTID)
    },

    formattedTerminationDate() {
      if (!this.terminationDate) return ""
      return format(this.terminationDate, "dd.MM.yyyy")
    },

    // Vorschau für Step 2 (Was wird alles gekündigt?)
    contractsToProcessPreview() {
      if (!this.activeContract) return []

      if (this.isMainContract) {
        // Hauptvertrag + Alle Addons (da automatisch)
        return [this.contractToTerminate, ...this.allAddons]
      } else {
        // Gewähltes Addon + Manuell selektierte Addons
        const others = this.otherAddons.filter((c) => this.selectedAddonIds.includes(c.CONTRACTID))
        return [this.contractToTerminate, ...others]
      }
    },
  },

  mounted() {
    this.fetchContracts()
  },

  watch: {
    // Sobald man ein Häkchen setzt/entfernt -> Text neu generieren
    selectedAddonIds() {
      // Wir aktualisieren immer, da jetzt beide Text-Varianten (Late & Regular) Paketnamen enthalten können
      this.mail.text = this.isLate ? this.getLateEmailHtml() : this.getRegularEmailHtml()
    },
  },

  methods: {
    async previewPdf() {
      const blob = await this.getPdfBlob()
      if (blob) window.open(URL.createObjectURL(blob))
    },
    formatDate(d) {
      if (!d) return ""
      return new Date(d).toLocaleDateString()
    },
    getContractColor(c) {
      if (c.CONTRACTTYP === "Hauptvertrag") return c.CONTRACTSTATE === 1 ? "green" : "orange"
      return "blue" // Farbe für Zusatzpakete
    },

    resetWizard() {
      this.step = 0
      this.activeContract = null
      this.contractToTerminate = null
      this.selectedContractIndex = null
      this.selectedAddonIds = []
      this.terminatedContractsResult = []
    },

    // Override für "Trotzdem rechtzeitig"
    forceOnTime() {
      this.isLate = false
      this.terminationDate = new Date(this.activeContract.TERM_ENDDATE)
      this.contractToTerminate = this.activeContract
      this.mail.text = this.getRegularEmailHtml()
      this.$toast.success("Status auf 'Fristgerecht' gesetzt")
    },

    async fetchContracts() {
      this.loadingContracts = true
      try {
        let mail = this.user.email
        let roodyUser = await api.getUserForManagerByEmail(mail)
        if (roodyUser.status && roodyUser.status === "userNotFound") {
          this.$emit("roodyUserNotFound")
          return
        }
        this.roodyUser = roodyUser
        let userId = roodyUser._id

        let res = await tacApi.fetchTACContractDetails(userId)
        res = res.map((e) => {
          return {
            mappedStudio: this.storeStudios.find((s) => s.tacPropertyId && s.tacPropertyId.toString().trim() === e.RESSORTGROUPID.toString().trim()),
            ...e,
          }
        })
        this.rawContracts = res || []
      } catch (e) {
        console.error(e)
      } finally {
        this.loadingContracts = false
      }
    },

    async selectContract(contract) {
      this.activeContract = contract
      this.selectedAddonIds = []
      this.step = 1
      this.loadingDetails = true

      this.calculateTerminationDetails()

      try {
        const response = await tacApi.getTACReasons(contract.RESSORTGROUPID, "MEMBERSHIP_TERMINATION")
        if (response && response.Reasons && response.Reasons.Reason) {
          this.reasons = response.Reasons.Reason.map((r) => ({
            ReasonID: parseInt(r.ReasonID.UniqueID[0].value, 10),
            Name: r.Reasons.TextElement.find((t) => t.language === "de")?.value || r.Reasons.TextElement[0]?.value,
          })).filter((reason, index, self) => index === self.findIndex((t) => t.Name === reason.Name))
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.loadingDetails = false
      }
    },

    calculateTerminationDetails() {
      const CANCELLATION_PERIOD_DAYS = 44
      const today = new Date()
      const currentTermEnd = new Date(this.activeContract.TERM_ENDDATE)
      const deadline = addDays(currentTermEnd, -CANCELLATION_PERIOD_DAYS)

      this.isLate = isAfter(today, deadline)

      if (this.isLate) {
        // Bei Zusatzverträgen gibt es meist keinen "Folgevertrag" im klassischen Sinne
        // Wenn Hauptvertrag -> Suche Folge-Hauptvertrag
        // Wenn Zusatzvertrag -> Verlängerung oft im selben Objekt oder neuem Eintrag?
        // Wir nutzen hier vereinfacht das selbe Objekt + 1 Jahr Logik falls kein expliziter Folgevertrag gefunden wird.

        const nextContract = this.rawContracts
          .filter((c) => c.CONTRACTTYP === this.activeContract.CONTRACTTYP && new Date(c.TERM_STARTDATE) > currentTermEnd)
          .sort((a, b) => new Date(a.TERM_STARTDATE) - new Date(b.TERM_STARTDATE))[0]

        if (nextContract) {
          this.contractToTerminate = nextContract
          this.terminationDate = new Date(nextContract.TERM_ENDDATE)
        } else {
          // Fallback: Selber Vertrag aber Enddatum in Zukunft projiziert
          this.contractToTerminate = this.activeContract // Wir müssen technisch diesen kündigen, aber per späterem Datum?
          // TAC API Logic: TerminateContract löscht ihn. Bei Late -> wir brauchen Lösung.
          // Wahrscheinlich akzeptiert TAC kein Delete wenn Frist vorbei.
          // Hier im Frontend simulieren wir es für die Mail.

          let years = 1
          if (this.activeContract.NAME.includes("2 Jahr")) years = 2
          this.terminationDate = addYears(currentTermEnd, years)
        }
        this.mail.text = this.getLateEmailHtml()
      } else {
        this.terminationDate = currentTermEnd
        this.contractToTerminate = this.activeContract
        this.mail.text = this.getRegularEmailHtml()
      }
      this.mail.to = this.user.email
    },

    async executeTermination() {
      this.processing = true
      this.terminatedContractsResult = []

      try {
        if (this.isMainContract) {
          // FALL A: Hauptvertrag
          // API Call NUR für Hauptvertrag (TAC erledigt Rest automatisch)
          await this.terminateSingle(this.contractToTerminate)

          // Für PDF/Mail merken wir uns aber ALLE, weil sie ja weg sind
          this.terminatedContractsResult = [this.contractToTerminate, ...this.allAddons]
        } else {
          // FALL B: Zusatzverträge
          // Wir müssen jeden einzeln kündigen (Selected + Current)
          const targets = [this.contractToTerminate]
          // Add manuell selected
          const others = this.otherAddons.filter((c) => this.selectedAddonIds.includes(c.CONTRACTID))
          targets.push(...others)

          // Loop API Calls
          for (const t of targets) {
            await this.terminateSingle(t)
          }
          this.terminatedContractsResult = targets
        }

        this.step = 3
      } catch (e) {
        this.$toast.error("Fehler: " + e.message)
      } finally {
        this.processing = false
      }
    },

    async terminateSingle(contract) {
      const data = {
        ResortId: contract.RESSORTGROUPID,
        ContractId: contract.CONTRACTID,
        ReasonId: this.selectedReasonId,
        ReasonType: "MEMBERSHIP_TERMINATION",
        CheckTermination: false,
      }

      const response = await tacApi.terminateTACContract(data)
      if (response?.Result?.resultStatusFlag !== "SUCCESS") {
        throw new Error(`Fehler bei ${contract.NAME}: ${response?.Result?.Text?.[0]?.value || "Unbekannt"}`)
      }
    },

    async sendTerminationEmail() {
      this.sendingMail = true
      try {
        const blob = await this.getPdfBlob()
        if (!blob) throw new Error("PDF Generierung fehlgeschlagen")

        const base64 = await this.blobToBase64(blob)
        const htmlBody = this.mail.text.replace(/\n/g, "<br>")

        const mailData = {
          to: this.mail.to,
          subject: this.mail.subject,
          html: htmlBody,
          attachments: [
            {
              filename: "Kündigungsbestätigung.pdf",
              content: base64.split(",")[1],
              encoding: "base64",
              contentType: "application/pdf",
            },
          ],
        }
        await api.sendTerminationMail(mailData)
        this.$toast.success("Bestätigung gesendet!")
        this.$emit("completed")
      } catch (e) {
        this.$toast.error("Send Error: " + e.message)
      } finally {
        this.sendingMail = false
      }
    },

    async getPdfBlob() {
      this.pdfLoading = true
      try {
        // Wir nutzen die Result-Liste für das PDF
        const list = this.terminatedContractsResult.length > 0 ? this.terminatedContractsResult : [this.activeContract]

        return await api.generateTerminationPdf({
          user: this.roodyUser,
          contractsToDisplay: list,
          terminationDate: this.formattedTerminationDate,
          isLate: this.isLate,
        })
      } catch (e) {
        console.error(e)
        return null
      } finally {
        this.pdfLoading = false
      }
    },

    blobToBase64(blob) {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.readAsDataURL(blob)
      })
    },

    getRegularEmailHtml() {
      // Namen aller gewählten Pakete sammeln
      const names = this.contractsToProcessPreview.map((c) => c.NAME).join(", ")

      const firstName = this.user.firstName

      // FALL A: Nur Zusatzpakete (Kürzer & freundlicher)
      if (!this.isMainContract) {
        return `Hallo ${firstName}\n\nIm Anhang senden wir dir die Kündigungsbestätigung des Zusatzpakets ${names}.\n\nWir wünschen dir weiterhin viel Freude im Training.\n\nSportliche Grüsse\nwell come FIT Team`
      }

      // FALL B: Hauptvertrag (Standardtext wie bisher)
      return `Hallo ${firstName}\n\nAnbei senden wir dir die Kündigungsbestätigung für: ${names}.\n\nWir bedauern deinen Entscheid sehr. Wir sind stets daran, uns zu verbessern. Dürfen wir dich bitten, uns den Grund für Deine Kündigung kurz mitzuteilen?\n\nWir würden uns freuen, dich in Zukunft wieder bei uns begrüssen zu dürfen.\n\nSportliche Grüsse\nwell come FIT Team`
    },

    getLateEmailHtml() {
      const firstName = this.user.firstName

      // FALL A: Nur Zusatzpakete (Erklärung zur Verlängerung + Bestätigung zum Enddatum)
      if (!this.isMainContract) {
        const names = this.contractsToProcessPreview.map((c) => c.NAME).join(", ")
        return `Hallo ${firstName}\n\nVertragsanpassungen müssen 45 Tage vor Vertragsablauf schriftlich gemeldet werden. Dein Abonnement hat sich bereits verlängert. Deshalb können wir dir die Kündigung des Zusatzpakets ${names} per ${this.formattedTerminationDate} bestätigen.\n\nWir bedanken uns für die Kenntnisnahme und wünschen dir viel Freude im Training.\n\nSportliche Grüsse\nwell come FIT AG`
      }

      // FALL B: Hauptvertrag (Standardtext mit Raten/Timestop Optionen)
      return (
        `Hallo ${firstName}\n\n` +
        `Gemäss unseren Mitgliedschaftsbedingungen verlängert sich die Mitgliedschaft automatisch, wenn diese nicht fristgerecht und schriftlich (spätestens 45 Tage vor Vertragsablauf) gekündigt wird (siehe Punkt 2 der Mitgliedschaftsbedingungen auf dem Vertrag).\n\n` +
        `Die Kündigungsfrist in deinem Fall ist bereits abgelaufen. Der Vertrag lässt sich nur per Vertragsablauf kündigen. Anbei senden wir dir die Kündigungsbestätigung per ${this.formattedTerminationDate}.\n\n` +
        `Bitte beachte, dass dein Mitgliedschaftsbeitrag somit auf Abonnementbeginn fällig ist.\n\n` +
        `Folgende Varianten können wir dir anbieten:\n\n` +
        `  •  Zahlung des Mitgliedschaftsbeitrages in Raten\n` +
        `     (2 oder 12 Raten - dafür benötigen wir einen neuen Vertrag, es wird ein Ratenzuschlag erhoben)\n\n` +
        `  •  Time-Stop\n` +
        `     (Unterbruch des Abonnements - gebührenpflichtig)\n` +
        `     die Zahlung des Mitgliedschaftsbeitrags bleibt bestehen / die Zahlungen der vereinbarten Raten bleibt bestehen\n\n` +
        `  •  Übergabe der Mitgliedschaft an ein Nichtmitglied (mit unterzeichneter, schriftlicher Bestätigung)\n` +
        `     (Umschreibegebühr und Aktivierungstarif total CHF 119.00)\n\n` +
        `Wir danken dir für deine Kenntnisnahme.\n\n` +
        `Sportliche Grüsse\n` +
        `well come FIT AG`
      )
    },
  },
}
</script>
